import * as React from 'react';
import EventEmitter from 'eventemitter3';
import {debounce} from 'lodash';
import SearchSuggestions from './SearchSuggestions';
import type {SearchConfig, QueryToken} from './types';
import {useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {TranslationDescriptor} from '@deviceinsight/ng-ui-scale-lib/api';

const hasInputTarget = (event: KeyboardEvent) =>
	//@ts-ignore TO DO!!
	event.target.tagName && ['INPUT', 'SELECT', 'TEXTAREA'].includes(event.target.tagName);

const normalizeEventKeys = (key: string) => {
	switch (key) {
		case 'Up':
			return 'ArrowUp';

		case 'Down':
			return 'ArrowDown';

		default:
			return key;
	}
};

type OwnProps = {
	query: string;
	queryTokens: Array<QueryToken> | null | undefined;
	submitted: boolean;
	onChange: (query: string) => void;
	onSubmit: (query: string) => void;
	autofocus: boolean;
	config?: SearchConfig<any>;
	placeholder: (TranslationDescriptor | null | undefined) | string;
};
const SearchInput: React.FC<OwnProps> = ({
	config,
	query,
	queryTokens,
	submitted,
	placeholder,
	onChange,
	onSubmit,
	autofocus
}) => {
	const {t} = useTranslation();
	const [hasFocus, setHasFocus] = React.useState(false);
	const [suggestionsQuery, setSuggestionsQuery] = React.useState('');
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const keyboardEventEmitter = React.useRef(new EventEmitter());

	const setSuggestionsQueryDebounced = React.useCallback((query: string) => {
		debounce((q: string) => setSuggestionsQuery(q), 300)(query);
	}, []);

	const inputKeyEventHandler = React.useCallback(
		(event: KeyboardEvent) => {
			const eventWasHandled = keyboardEventEmitter.current.emit(normalizeEventKeys(event.key));

			if (eventWasHandled) {
				event.preventDefault();
			} else if (event.key === 'Enter') {
				onSubmit(query);
			}
		},
		[onSubmit, query]
	);

	const inputFocusEventHandler = React.useCallback(() => {
		setHasFocus(true);
	}, []);

	const inputBlurEventHandler = React.useCallback(() => {
		setHasFocus(false);
	}, []);

	const windowKeyEventHandler = React.useCallback((event: KeyboardEvent) => {
		if (event.key === '/' && !hasInputTarget(event) && inputRef.current) {
			inputRef.current.focus();
			event.preventDefault();
		}
	}, []);

	const placeholderToString = React.useCallback(
		(placeholder: string | (TranslationDescriptor | null | undefined)): string | null => {
			if (!placeholder) {
				return null;
			}

			if (typeof placeholder === 'string') {
				return placeholder;
			}

			return t(placeholder.id, {
				defaultValue: placeholder.defaultValue
			});
		},
		[t]
	);

	// Setup event listeners
	React.useEffect(() => {
		const inputElement = inputRef.current;

		if (inputElement) {
			inputElement.addEventListener('keydown', inputKeyEventHandler);
			inputElement.addEventListener('focus', inputFocusEventHandler);
			inputElement.addEventListener('blur', inputBlurEventHandler);

			if (autofocus) {
				window.addEventListener('keydown', windowKeyEventHandler);
				inputElement.focus();
			}
		}

		return () => {
			if (inputElement) {
				inputElement.removeEventListener('keydown', inputKeyEventHandler);
				inputElement.removeEventListener('focus', inputFocusEventHandler);
				inputElement.removeEventListener('blur', inputBlurEventHandler);

				if (autofocus) {
					window.removeEventListener('keydown', windowKeyEventHandler);
				}
			}
		};
	}, [inputKeyEventHandler, inputFocusEventHandler, inputBlurEventHandler, windowKeyEventHandler, autofocus]);

	// Update suggestions query when query changes
	React.useEffect(() => {
		setSuggestionsQueryDebounced(query);
	}, [query, setSuggestionsQueryDebounced]);

	const showSuggestions = inputRef.current && hasFocus && query.length > 0 && queryTokens && !submitted;

	return (
		<span className="search-input">
			<input
				value={query}
				placeholder={
					placeholderToString(placeholder) ||
					t('search.placeholder', {
						defaultValue: 'Search'
					})
				}
				className="search-input-field"
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize="off"
				spellCheck="false"
				type="text"
				onChange={(e) => onChange(e.target.value)}
				onFocus={(e) => e.target.select()}
				ref={inputRef}
				data-testid="global-search-input"
			/>
			{showSuggestions && config && (
				<SearchSuggestions
					config={config}
					query={suggestionsQuery}
					submitted={submitted}
					queryTokens={queryTokens}
					changeQuery={onChange}
					keyboardEventEmitter={keyboardEventEmitter.current}
				/>
			)}
		</span>
	);
};

export default SearchInput;
